import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { Separator } from "@/components/ui/separator";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Loader2, KeyRound, RefreshCw, Plug, PlugZap, Copy, Play, Send,
  Trash2, ShieldCheck, ShieldAlert, Users2, Plus,
} from "lucide-react";

const SYNC_MODES = [
  { value: "bidirectional", label: "Bidirectional (recommended)" },
  { value: "pull_only", label: "Pull only (Connecteam → Quanta)" },
  { value: "push_only", label: "Push only (Quanta → Connecteam)" },
  { value: "off", label: "Paused" },
];

function fmt(ts) {
  if (!ts) return "—";
  try { return new Date(ts).toLocaleString(); } catch (_e) { return ts; }
}

export default function IntegrationsPage() {
  return (
    <div className="mx-auto max-w-5xl space-y-6 p-6" data-testid="integrations-page">
      <header className="flex flex-col gap-1">
        <div className="text-xs uppercase tracking-wide text-muted-foreground">Workspace admin</div>
        <h1 className="text-3xl font-semibold tracking-tight" style={{ color: "var(--quanta-navy)" }}>
          Integrations
        </h1>
        <p className="text-sm text-muted-foreground">
          Wire third-party apps into Quanta. Connect once — data flows both ways.
        </p>
      </header>
      <Tabs defaultValue="connecteam" className="w-full">
        <TabsList data-testid="integrations-tabs">
          <TabsTrigger value="connecteam" data-testid="tab-connecteam">
            <Plug className="mr-1.5 h-4 w-4" /> Connecteam
          </TabsTrigger>
          <TabsTrigger value="scim" data-testid="tab-scim">
            <Users2 className="mr-1.5 h-4 w-4" /> SCIM 2.0
          </TabsTrigger>
        </TabsList>
        <TabsContent value="connecteam" className="mt-6">
          <ConnecteamPanel />
        </TabsContent>
        <TabsContent value="scim" className="mt-6">
          <ScimPanel />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function ConnecteamPanel() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [apiKeyInput, setApiKeyInput] = useState("");
  const [syncing, setSyncing] = useState(false);
  const [pushing, setPushing] = useState(false);
  const [rotating, setRotating] = useState(false);
  const [pullMinutes, setPullMinutes] = useState(15);
  const [activity, setActivity] = useState([]);
  const [health, setHealth] = useState(null);
  const [mappingCounts, setMappingCounts] = useState(null);
  const [lastJob, setLastJob] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [cfgResp, actResp, countsResp] = await Promise.all([
        api.get("/tenant/integrations/connecteam"),
        api.get("/tenant/integrations/connecteam/activity?limit=20"),
        api.get("/tenant/integrations/connecteam/mapping-counts").catch(() => ({ data: null })),
      ]);
      setCfg(cfgResp.data);
      setPullMinutes(cfgResp.data.pull_interval_minutes || 15);
      setActivity(actResp.data.items || []);
      if (countsResp?.data) setMappingCounts(countsResp.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const saveConfig = async (patch) => {
    setSaving(true);
    try {
      const { data } = await api.patch("/tenant/integrations/connecteam", patch);
      setCfg(data);
      setTestResult(null);
      toast.success("Connecteam config saved");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const saveKey = async () => {
    if (!apiKeyInput.trim()) return;
    await saveConfig({ api_key: apiKeyInput.trim() });
    setApiKeyInput("");
  };

  const clearKey = async () => {
    await saveConfig({ api_key: "" });
  };

  const testConnection = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await api.post("/tenant/integrations/connecteam/test");
      setTestResult(data);
      if (data.ok) toast.success(`Connected — ${data.total_users ?? "?"} users`);
      else toast.error(`Test failed: ${data.error}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setTesting(false);
    }
  };

  const rotateWebhook = async () => {
    setRotating(true);
    try {
      const { data } = await api.post(
        "/tenant/integrations/connecteam/rotate-webhook",
      );
      setCfg((c) => ({ ...c, webhook_secret_token: data.webhook_secret_token }));
      toast.success("Webhook token rotated — update your Connecteam webhook target URL.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setRotating(false);
    }
  };

  const syncNow = async (scope) => {
    setSyncing(true);
    try {
      const { data } = await api.post(
        "/tenant/integrations/connecteam/sync",
        { scope },
      );
      setLastJob({ kind: "sync", ...data });
      pollJob(data.job_id);
      toast.success(`Sync started · ${scope}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSyncing(false);
    }
  };

  const pushNow = async () => {
    setPushing(true);
    try {
      const { data } = await api.post(
        "/tenant/integrations/connecteam/push",
        { scope: "current_week" },
      );
      setLastJob({ kind: "push", ...data });
      pollJob(data.job_id);
      toast.success("Push queued");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setPushing(false);
    }
  };

  const pollJob = (jobId) => {
    let attempts = 0;
    const tick = async () => {
      attempts += 1;
      if (attempts > 60) return;
      try {
        const { data } = await api.get(
          `/tenant/integrations/connecteam/jobs/${jobId}`,
        );
        setLastJob((j) => (j?.job_id === jobId ? { ...j, ...data } : j));
        if (data.status === "ok" || data.status === "failed") {
          load();
          return;
        }
      } catch (_e) { /* ignore */ }
      setTimeout(tick, 750);
    };
    tick();
  };

  const copyWebhookUrl = () => {
    const url = webhookUrl();
    if (!url) return;
    navigator.clipboard.writeText(url).then(() => {
      toast.success("Webhook URL copied");
    });
  };

  const webhookUrl = () => {
    if (!cfg?.tenant_id || !cfg?.webhook_secret_token) return "";
    const base = process.env.REACT_APP_BACKEND_URL || window.location.origin;
    return `${base}/api/webhooks/connecteam/${cfg.tenant_id}?token=${cfg.webhook_secret_token}`;
  };

  if (loading || !cfg) {
    return (
      <div className="flex items-center gap-2 rounded-xl border bg-white p-6 text-sm text-muted-foreground"
           data-testid="integrations-loading">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading Connecteam…
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="connecteam-panel">
      {/* Status bar */}
      <div
        className="flex flex-col gap-3 rounded-2xl border p-4 sm:flex-row sm:items-center sm:justify-between"
        style={{ background: "var(--quanta-canvas, #F7F9FC)", borderColor: "var(--quanta-border)" }}
      >
        <div className="flex items-center gap-3">
          <div
            className="grid h-10 w-10 place-items-center rounded-xl"
            style={{ background: "var(--quanta-navy)", color: "white" }}
          >
            <PlugZap className="h-5 w-5" />
          </div>
          <div>
            <div className="text-base font-semibold" style={{ color: "var(--quanta-navy)" }}>
              Connecteam
            </div>
            <div className="text-xs text-muted-foreground">
              {cfg.has_api_key
                ? <>API key set — prefix <code className="font-mono">{cfg.api_key_prefix}•••</code></>
                : "Not connected"}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge
            variant="outline"
            className={cfg.enabled ? "border-emerald-500/40 text-emerald-700" : "border-slate-300 text-slate-600"}
            data-testid="ct-enabled-badge"
          >
            {cfg.enabled ? "Enabled" : "Disabled"}
          </Badge>
          <Badge variant="outline" data-testid="ct-sync-mode-badge">
            {cfg.sync_mode}
          </Badge>
        </div>
      </div>

      {/* API key */}
      <div className="rounded-2xl border bg-white p-4">
        <div className="mb-2 flex items-center gap-2">
          <KeyRound className="h-4 w-4" style={{ color: "var(--quanta-navy)" }} />
          <div className="text-sm font-semibold" style={{ color: "var(--quanta-navy)" }}>
            API key
          </div>
        </div>
        <p className="mb-3 text-xs text-muted-foreground">
          Get a key at <a className="underline" href="https://app.connecteam.com/dashboard/api-keys"
            target="_blank" rel="noreferrer">app.connecteam.com → Settings → API Keys</a>.
          Only the prefix is shown after save; the full key is encrypted (Fernet + MFA_ENC_KEY).
        </p>
        <div className="flex flex-col gap-2 sm:flex-row">
          <Input
            type="password"
            placeholder="ct_live_…"
            value={apiKeyInput}
            onChange={(e) => setApiKeyInput(e.target.value)}
            data-testid="ct-api-key-input"
            autoComplete="off"
          />
          <Button onClick={saveKey} disabled={saving || !apiKeyInput.trim()} data-testid="ct-save-key-btn">
            {saving ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}
            Save
          </Button>
          <Button variant="outline" onClick={testConnection}
                  disabled={testing || !cfg.has_api_key}
                  data-testid="ct-test-btn">
            {testing ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}
            Test connection
          </Button>
          {cfg.has_api_key && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="ghost" className="text-red-600" data-testid="ct-clear-btn">
                  <Trash2 className="mr-1 h-4 w-4" /> Disconnect
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Disconnect Connecteam?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This clears the API key, disables sync, and soft-flags every
                    mapping. Existing data stays; syncs stop.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={clearKey} data-testid="ct-clear-confirm">
                    Disconnect
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
        {testResult && (
          <div className={"mt-3 flex items-start gap-2 rounded-lg border p-3 text-xs " +
            (testResult.ok ? "border-emerald-500/40 bg-emerald-500/5"
                           : "border-red-500/40 bg-red-500/5")}
               data-testid="ct-test-result">
            {testResult.ok
              ? <ShieldCheck className="mt-0.5 h-4 w-4 text-emerald-600" />
              : <ShieldAlert className="mt-0.5 h-4 w-4 text-red-600" />}
            <div>
              {testResult.ok
                ? `Connected — ${testResult.total_users ?? "?"} users, latency ${testResult.latency_ms}ms`
                : `Failed — ${testResult.error}`}
            </div>
          </div>
        )}
      </div>

      {/* Mode + interval */}
      <div className="grid gap-4 md:grid-cols-2">
        <div className="rounded-2xl border bg-white p-4">
          <div className="mb-2 text-sm font-semibold" style={{ color: "var(--quanta-navy)" }}>
            Sync mode
          </div>
          <Select
            value={cfg.sync_mode}
            onValueChange={(v) => saveConfig({ sync_mode: v })}
          >
            <SelectTrigger data-testid="ct-sync-mode-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {SYNC_MODES.map((m) => (
                <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="mt-3 flex items-center gap-2">
            <Switch
              checked={cfg.enabled}
              onCheckedChange={(v) => saveConfig({ enabled: v })}
              data-testid="ct-enable-switch"
              disabled={!cfg.has_api_key}
            />
            <Label className="text-sm">Enable Connecteam sync</Label>
          </div>
        </div>
        <div className="rounded-2xl border bg-white p-4">
          <div className="mb-2 text-sm font-semibold" style={{ color: "var(--quanta-navy)" }}>
            Pull interval
          </div>
          <div className="flex items-center gap-3">
            <div className="flex-1">
              <Slider
                min={5} max={60} step={1}
                value={[pullMinutes]}
                onValueChange={(v) => setPullMinutes(v[0])}
                onValueCommit={(v) => saveConfig({ pull_interval_minutes: v[0] })}
                data-testid="ct-pull-slider"
              />
            </div>
            <div className="w-16 text-right text-sm font-mono" data-testid="ct-pull-value">
              {pullMinutes}m
            </div>
          </div>
          <p className="mt-2 text-xs text-muted-foreground">
            Scheduled delta pull runs every {pullMinutes} minutes. Webhooks
            deliver in real time — the scheduler just fills any gaps.
          </p>
        </div>
      </div>

      {/* Webhook URL */}
      <div className="rounded-2xl border bg-white p-4">
        <div className="mb-2 text-sm font-semibold" style={{ color: "var(--quanta-navy)" }}>
          Webhook target URL
        </div>
        <p className="mb-3 text-xs text-muted-foreground">
          Register this in Connecteam under <strong>Settings → Webhooks → New</strong>.
          Add BOTH: (1) a <code>users</code> webhook with no <code>objectId</code>,
          and (2) a <code>time_activity</code> webhook per time-clock — its <code>objectId</code> is the time-clock id.
        </p>
        <div className="flex flex-col gap-2 sm:flex-row">
          <Input
            readOnly value={webhookUrl()}
            data-testid="ct-webhook-url"
            className="font-mono text-xs"
          />
          <Button variant="outline" onClick={copyWebhookUrl} data-testid="ct-copy-webhook">
            <Copy className="mr-1 h-4 w-4" /> Copy
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="ghost" data-testid="ct-rotate-webhook">
                <RefreshCw className="mr-1 h-4 w-4" /> Rotate
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Rotate webhook token?</AlertDialogTitle>
                <AlertDialogDescription>
                  The old URL will stop accepting deliveries immediately. You
                  must re-register the new URL in every Connecteam webhook.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={rotateWebhook} data-testid="ct-rotate-confirm">
                  {rotating ? "Rotating…" : "Rotate"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      {/* Actions row */}
      <div className="rounded-2xl border bg-white p-4">
        <div className="mb-3 text-sm font-semibold" style={{ color: "var(--quanta-navy)" }}>
          On-demand actions
        </div>
        <div className="flex flex-wrap gap-2">
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                disabled={!cfg.enabled || syncing}
                data-testid="ct-sync-now-btn"
                style={{ backgroundColor: "var(--quanta-blue)" }}
              >
                {syncing ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Play className="mr-1 h-4 w-4" />}
                Sync now
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Sync from Connecteam</AlertDialogTitle>
                <AlertDialogDescription>
                  <b>Delta</b> pulls only rows changed since the last sync (fast).
                  <b>Full</b> re-scans everything (slow — use once, or after major
                  changes on the Connecteam side).
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter className="gap-2">
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => syncNow("full")}
                  data-testid="ct-sync-full-confirm"
                >
                  Full
                </AlertDialogAction>
                <AlertDialogAction
                  onClick={() => syncNow("delta")}
                  data-testid="ct-sync-delta-confirm"
                >
                  Delta
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          <Button
            variant="outline"
            onClick={pushNow}
            disabled={!cfg.enabled || pushing || cfg.sync_mode === "pull_only"}
            data-testid="ct-push-now-btn"
          >
            {pushing ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Send className="mr-1 h-4 w-4" />}
            Push approved timesheets now
          </Button>
        </div>
        {lastJob && (
          <div className="mt-3 rounded-lg border p-3 text-xs" data-testid="ct-last-job">
            <div className="mb-1 font-medium">
              {lastJob.kind === "push" ? "Push" : "Sync"} · {lastJob.status}
            </div>
            <div className="grid grid-cols-3 gap-2 tabular-nums">
              <span>added: <b>{lastJob.records_added ?? 0}</b></span>
              <span>updated: <b>{lastJob.records_updated ?? 0}</b></span>
              <span>skipped: <b>{lastJob.records_skipped ?? 0}</b></span>
            </div>
          </div>
        )}
      </div>

      {/* Status card */}
      <div className="rounded-2xl border bg-white p-4">
        <div className="mb-2 text-sm font-semibold" style={{ color: "var(--quanta-navy)" }}>
          Status
        </div>
        <div className="grid grid-cols-2 gap-3 text-sm md:grid-cols-4">
          <StatusCell label="Last pull" value={fmt(cfg.last_pull_at)} testid="ct-last-pull" />
          <StatusCell label="Last push" value={fmt(cfg.last_push_at)} testid="ct-last-push" />
          <StatusCell label="Connected by" value={cfg.connected_at ? fmt(cfg.connected_at) : "—"} />
          <StatusCell
            label="Last error"
            value={cfg.last_error ? cfg.last_error.slice(0, 60) : "none"}
            testid="ct-last-error"
            danger={!!cfg.last_error}
          />
        </div>
        {/* Phase 16 polish — explicit mapping-count labels (screen-reader legible). */}
        <div
          className="mt-3 grid grid-cols-1 gap-3 border-t pt-3 text-sm sm:grid-cols-3"
          data-testid="ct-mapping-counts"
          role="group"
          aria-label="Connecteam mapping counts"
        >
          <MappingCountCell
            label="Mapped users"
            value={mappingCounts?.users ?? 0}
            testid="ct-mapped-users"
          />
          <MappingCountCell
            label="Mapped worksites"
            value={mappingCounts?.worksites ?? 0}
            testid="ct-mapped-worksites"
          />
          <MappingCountCell
            label="Mapped entries"
            value={mappingCounts?.entries ?? 0}
            testid="ct-mapped-entries"
          />
        </div>
      </div>

      {/* Activity feed */}
      <div className="rounded-2xl border bg-white p-4">
        <div className="mb-2 flex items-center justify-between">
          <div className="text-sm font-semibold" style={{ color: "var(--quanta-navy)" }}>
            Recent activity
          </div>
          <Badge variant="outline" className="text-[10px]">{activity.length}</Badge>
        </div>
        {activity.length === 0 ? (
          <p className="text-xs text-muted-foreground">
            No events yet — trigger a Sync or wait for the next webhook.
          </p>
        ) : (
          <ul className="divide-y text-xs" data-testid="ct-activity-list">
            {activity.map((e) => (
              <li key={e.id} className="py-2" data-testid={`ct-activity-row-${e.id}`}>
                <div className="flex items-baseline justify-between gap-2">
                  <div className="font-mono text-[11px]">{e.event_type}</div>
                  <span className="tabular-nums text-[11px] text-muted-foreground">
                    {fmt(e.created_at)}
                  </span>
                </div>
                {e.metadata && Object.keys(e.metadata).length > 0 && (
                  <div className="mt-0.5 max-w-full truncate text-[11px] text-muted-foreground">
                    {JSON.stringify(e.metadata)}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      <Separator />

      <div className="rounded-2xl border p-4 text-xs text-muted-foreground"
           style={{ background: "var(--quanta-canvas, #F7F9FC)" }}>
        <div className="mb-1 text-sm font-semibold" style={{ color: "var(--quanta-navy)" }}>
          Setup checklist
        </div>
        <ol className="ml-4 list-decimal space-y-1">
          <li>Paste an <b>API key</b> from Connecteam and press <b>Test connection</b>.</li>
          <li>Pick a <b>sync mode</b> — bidirectional is the most useful.</li>
          <li>Copy the <b>webhook URL</b> above and register two webhooks in
              Connecteam: one for <code>users</code>, one for <code>time_activity</code>
              per time-clock.</li>
          <li>Click <b>Sync now → Full</b> once, then let the scheduler take over.</li>
        </ol>
      </div>
    </div>
  );
}

function StatusCell({ label, value, testid, danger }) {
  return (
    <div className="rounded-lg border p-2" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={"mt-0.5 font-mono text-[11px] " + (danger ? "text-red-600" : "")}>
        {value}
      </div>
    </div>
  );
}

function MappingCountCell({ label, value, testid }) {
  // Phase 16 polish — explicit <label>+<span> pair so screen readers
  // announce both the field name and value, and the numeric count is
  // visually prominent alongside its label.
  return (
    <div className="flex items-center justify-between rounded-lg border bg-white/60 px-3 py-2" data-testid={testid}>
      <label htmlFor={testid + "-value"} className="text-xs font-medium text-muted-foreground">
        {label}
      </label>
      <span
        id={testid + "-value"}
        className="tabular-nums text-sm font-semibold"
        style={{ color: "var(--quanta-navy)" }}
      >
        {label}: <b>{value}</b>
      </span>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Phase 17b — SCIM 2.0 tab
// ---------------------------------------------------------------------------
function ScimPanel() {
  const [tokens, setTokens] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [reveal, setReveal] = useState(null);
  const [confirmRevoke, setConfirmRevoke] = useState(null);
  const scimBaseUrl = `${process.env.REACT_APP_BACKEND_URL}/api/scim/v2/`;

  const load = async () => {
    setLoading(true);
    try {
      const [t, m] = await Promise.all([
        api.get("/tenant/scim-tokens"),
        api.get("/tenant/scim/group-mappings"),
      ]);
      setTokens(t.data.items || []);
      setMappings(m.data.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const copyUrl = () => {
    navigator.clipboard.writeText(scimBaseUrl);
    toast.success("SCIM URL copied");
  };

  const revoke = async (t) => {
    try {
      await api.delete(`/tenant/scim-tokens/${t.id}`);
      toast.success(`Revoked ${t.name}`);
      setConfirmRevoke(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const setMapping = async (m, role) => {
    try {
      await api.put(`/tenant/scim/group-mappings/${m.scim_group_id}`, { quanta_role: role });
      toast.success(`Mapped ${m.display_name} → ${role}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="scim-panel">
      {/* Setup instructions */}
      <div className="rounded-2xl border bg-white p-4" data-testid="scim-setup-block">
        <div className="mb-2 flex items-center gap-2 text-sm font-semibold" style={{ color: "var(--quanta-navy)" }}>
          <ShieldCheck className="h-4 w-4" />
          Configure SCIM 2.0 in your IdP
        </div>
        <p className="mb-3 text-xs text-muted-foreground">
          Point Okta, Azure AD, OneLogin, or any SCIM 2.0-compliant provisioning tool at Quanta using these credentials:
        </p>
        <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
          <div className="rounded-lg border p-3">
            <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">SCIM base URL</Label>
            <div className="mt-1 flex items-center gap-2">
              <code
                className="flex-1 truncate rounded bg-slate-50 px-2 py-1 text-xs"
                data-testid="scim-base-url"
              >
                {scimBaseUrl}
              </code>
              <Button size="sm" variant="outline" onClick={copyUrl} data-testid="btn-copy-scim-url">
                <Copy className="h-3 w-3" />
              </Button>
            </div>
          </div>
          <div className="rounded-lg border p-3">
            <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">Auth type</Label>
            <div className="mt-1 text-xs">OAuth Bearer Token (issue below)</div>
          </div>
        </div>
      </div>

      {/* Tokens */}
      <div className="rounded-2xl border bg-white p-4">
        <div className="mb-3 flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm font-semibold" style={{ color: "var(--quanta-navy)" }}>
            <KeyRound className="h-4 w-4" />
            SCIM bearer tokens
            <Badge variant="outline" data-testid="scim-tokens-count">{tokens.length}</Badge>
          </div>
          <Button size="sm" onClick={() => setShowCreate(true)} data-testid="btn-new-scim-token">
            <Plus className="mr-1 h-3 w-3" /> New token
          </Button>
        </div>
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading&hellip;
          </div>
        ) : tokens.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="scim-tokens-empty">
            No SCIM tokens yet. Create one to hand to your IdP&rsquo;s SCIM provisioning UI.
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Token</TableHead>
                <TableHead>Scopes</TableHead>
                <TableHead>Last used</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {tokens.map((t) => (
                <TableRow key={t.id} data-testid={`scim-token-row-${t.id}`}>
                  <TableCell className="font-medium">{t.name}</TableCell>
                  <TableCell className="font-mono text-xs">{t.masked_token}</TableCell>
                  <TableCell className="text-xs">{(t.scopes || []).join(" ")}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {t.last_used_at ? new Date(t.last_used_at).toLocaleString() : "never"}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {new Date(t.created_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell className="text-right">
                    {t.revoked_at ? (
                      <Badge variant="destructive">Revoked</Badge>
                    ) : (
                      <Button
                        size="sm" variant="destructive"
                        onClick={() => setConfirmRevoke(t)}
                        data-testid={`btn-revoke-scim-${t.id}`}
                      >
                        <Trash2 className="mr-1 h-3 w-3" /> Revoke
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Group → role mappings */}
      <div className="rounded-2xl border bg-white p-4" data-testid="scim-mappings-block">
        <div className="mb-3 flex items-center gap-2 text-sm font-semibold" style={{ color: "var(--quanta-navy)" }}>
          <Users2 className="h-4 w-4" />
          SCIM groups → Quanta roles
        </div>
        <p className="mb-3 text-xs text-muted-foreground">
          When your IdP pushes users into a SCIM group, Quanta grants them the mapped role automatically. The
          <code className="mx-1">master</code> role can never be granted via SCIM.
        </p>
        {mappings.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="scim-mappings-empty">
            No SCIM groups yet. Groups appear here once your IdP pushes them.
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>SCIM group</TableHead>
                <TableHead>Members</TableHead>
                <TableHead>Mapped role</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {mappings.map((m) => (
                <TableRow key={m.scim_group_id} data-testid={`scim-mapping-row-${m.scim_group_id}`}>
                  <TableCell className="font-medium">{m.display_name}</TableCell>
                  <TableCell className="tabular-nums text-sm">{m.member_count}</TableCell>
                  <TableCell>
                    <Select
                      value={m.quanta_role || "none"}
                      onValueChange={(v) => setMapping(m, v)}
                    >
                      <SelectTrigger
                        className="w-40"
                        data-testid={`scim-role-select-${m.scim_group_id}`}
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">— none —</SelectItem>
                        <SelectItem value="worker">worker</SelectItem>
                        <SelectItem value="approver">approver</SelectItem>
                        <SelectItem value="admin">admin</SelectItem>
                        <SelectItem value="tenant_admin">tenant_admin</SelectItem>
                      </SelectContent>
                    </Select>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <CreateScimTokenDialog
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={(payload) => {
          setShowCreate(false);
          setReveal(payload);
          load();
        }}
      />

      <ScimTokenReveal reveal={reveal} onClose={() => setReveal(null)} />

      <Dialog open={!!confirmRevoke} onOpenChange={(o) => !o && setConfirmRevoke(null)}>
        <DialogContent data-testid="dialog-confirm-revoke-scim">
          <DialogHeader>
            <DialogTitle>Revoke {confirmRevoke?.name}?</DialogTitle>
            <DialogDescription>
              After revocation, this token can no longer authenticate against Quanta&rsquo;s SCIM endpoints.
              Your IdP&rsquo;s next provisioning attempt will fail — reconfigure it with a new token.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmRevoke(null)}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={() => revoke(confirmRevoke)}
              data-testid="btn-confirm-revoke-scim"
            >
              Revoke
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CreateScimTokenDialog({ open, onClose, onCreated }) {
  const [name, setName] = useState("");
  const [scope, setScope] = useState("read_write");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    if (!name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      const scopes = scope === "read_only" ? ["read"] : ["read", "write"];
      const { data } = await api.post("/tenant/scim-tokens", { name: name.trim(), scopes });
      toast.success("SCIM token created");
      onCreated(data);
      setName(""); setScope("read_write");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="dialog-new-scim-token">
        <DialogHeader>
          <DialogTitle>New SCIM 2.0 token</DialogTitle>
          <DialogDescription>
            Give it a memorable name (typically the IdP + environment, e.g. &ldquo;Okta production&rdquo;).
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="scim-token-name">Name</Label>
            <Input
              id="scim-token-name"
              placeholder="Okta production"
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="input-scim-token-name"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="scim-token-scope">Scope</Label>
            <Select value={scope} onValueChange={setScope}>
              <SelectTrigger id="scim-token-scope" data-testid="select-scim-token-scope">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="read_write">Full read + write</SelectItem>
                <SelectItem value="read_only">Read-only</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="btn-create-scim-token">
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Create token
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ScimTokenReveal({ reveal, onClose }) {
  const copy = () => {
    if (!reveal?.token) return;
    navigator.clipboard.writeText(reveal.token);
    toast.success("Token copied — store it now");
  };
  return (
    <Dialog open={!!reveal} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="dialog-scim-token-reveal">
        <DialogHeader>
          <DialogTitle>SCIM token &mdash; {reveal?.name}</DialogTitle>
          <DialogDescription>
            Copy this token now. Quanta will never show it again — store it in your IdP&rsquo;s SCIM
            configuration and in your secrets vault.
          </DialogDescription>
        </DialogHeader>
        <div
          className="break-all rounded-md border bg-slate-50 p-3 font-mono text-xs"
          data-testid="scim-token-raw"
        >
          {reveal?.token}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={copy} data-testid="btn-copy-scim-token">
            <Copy className="mr-2 h-4 w-4" /> Copy token
          </Button>
          <Button onClick={onClose}>Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
